import React, { useState, useEffect, useContext, useMemo } from 'react';
import Iller from "../../il-ilce.json";
import countryCodesData from '../../constants/countryCodes.json';
import CountrySelector from '../CountrySelector';
import Modal from 'react-modal';
import './DeliveryForm.css';
import { InstitutionalContext } from '../../context/InstitutionalContext';
import { useToast } from '../../context/ToastContext';
import {
  useGetAddressesQuery,
  useAddAddressMutation,
  useUpdateAddressMutation
} from '../../services/userApi';

const DeliveryForm = ({
  referansKodu,
  onSubmitSuccess,
  isDepositInCart = false,
  initialFormData = {}
}) => {
  const { fetchInstitutional } = useContext(InstitutionalContext);
  const { showToast } = useToast();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentTerm, setCurrentTerm] = useState('');
  const [termContent, setTermContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDeliveryAddressId, setSelectedDeliveryAddressId] = useState('');
  const [selectedBillingAddressId, setSelectedBillingAddressId] = useState('');
  const [isBillingSame, setIsBillingSame] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressModalContext, setAddressModalContext] = useState('delivery');
  const [addressModalMode, setAddressModalMode] = useState('list');
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    id: null,
    title: "",
    ad: "",
    soyad: "",
    email: "",
    telefon: "",
    ulke_kodu: "TR",
    tc: "",
    dogum_tarihi: "",
    addressText: "",
    il: "",
    ilce: "",
    adres_tipi: "Teslimat Adresi"
  });

  // Sort country codes: Turkey first, then alphabetical
  const countryCodes = useMemo(() => {
    const turkey = countryCodesData.find(c => c.code === 'TR');
    const others = countryCodesData.filter(c => c.code !== 'TR').sort((a, b) => a.name.localeCompare(b.name));
    return [turkey, ...others];
  }, []);

  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]);
  const [selectedCountryForModal, setSelectedCountryForModal] = useState(countryCodes[0]);
  const [iller, setIller] = useState(Iller.iller || []);
  const [ilceler, setIlceler] = useState([]);
  const [manualIlceler, setManualIlceler] = useState([]);

  const getDefaultAddressForm = (adresTipi) => ({
    id: null,
    title: "",
    ad: "",
    soyad: "",
    email: "",
    telefon: "",
    ulke_kodu: "TR",
    tc: "",
    dogum_tarihi: "",
    addressText: "",
    il: "",
    ilce: "",
    adres_tipi: adresTipi
  });

  const getAdresTipiByContext = (context) =>
    context === 'billing' ? "Fatura Adresi" : "Teslimat Adresi";

  // Sözleşme durum state'leri kendi içimizde tanımlandı
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [salesTermsAccepted, setSalesTermsAccepted] = useState(false);
  const [deliveryTermsAccepted, setDeliveryTermsAccepted] = useState(false);

  // formData state'i artık kendi içimizde tanımlanıyor
  const [formData, setFormData] = useState({
    ad: initialFormData.ad || '',
    soyad: initialFormData.soyad || '',
    email: initialFormData.email || '',
    telefon: initialFormData.telefon || '',
    il: initialFormData.il || '',
    ilce: initialFormData.ilce || '',
    adres: initialFormData.adres || '',
    siparis_notu: initialFormData.siparis_notu || '',
    paymentType: initialFormData.paymentType || 'creditCard',
    tc: initialFormData.tc || '',
    dogum_tarihi: initialFormData.dogum_tarihi || ''
  });

  // Kullanıcı giriş durumunu kontrol et ve form verilerini ayarla
  useEffect(() => {
    const token = localStorage.getItem('token');
    const isUserLoggedIn = !!token;
    setIsLoggedIn(isUserLoggedIn);

    // Eğer kullanıcı giriş yapmışsa ve initialFormData varsa, formu doldur
    if (isUserLoggedIn && Object.keys(initialFormData).length > 0) {
      setFormData(prev => ({
        ...prev,
        ad: initialFormData.ad || prev.ad,
        soyad: initialFormData.soyad || prev.soyad,
        email: initialFormData.email || prev.email,
        telefon: initialFormData.telefon || prev.telefon,
        il: initialFormData.il || prev.il,
        ilce: initialFormData.ilce || prev.ilce,
        adres: initialFormData.adres || prev.adres,
        tc: initialFormData.tc || initialFormData.tckimlik || prev.tc,
        dogum_tarihi: initialFormData.dogum_tarihi || prev.dogum_tarihi,
        siparis_notu: initialFormData.siparis_notu || prev.siparis_notu,
        paymentType: initialFormData.paymentType || prev.paymentType
      }));

      // Eğer ülke kodu varsa seçili ülkeyi ayarla
      if (initialFormData.ulke_kodu) {
        const country = countryCodes.find(c => c.code === initialFormData.ulke_kodo);
        if (country) {
          setSelectedCountry(country);
          setSelectedCountryForModal(country);
        }
      }
    }
  }, [initialFormData]);

  // RTK Query ile adresleri çek
  const {
    data: addressesData,
    isLoading: addressesLoading,
    error: addressesError,
    refetch: refetchAddresses
  } = useGetAddressesQuery(undefined, { skip: !isLoggedIn });
  const [addAddress, { isLoading: isAddingAddress }] = useAddAddressMutation();
  const [updateAddress, { isLoading: isUpdatingAddress }] = useUpdateAddressMutation();

  // addresses state'ini RTK Query'den gelen veriyle güncelle
  const addresses = Array.isArray(addressesData) ? addressesData : [];
  const deliveryAddresses = useMemo(() => addresses.filter(address => address.adres_tipi === "Teslimat Adresi"), [addresses]);
  const billingAddresses = useMemo(() => addresses.filter(address => address.adres_tipi === "Fatura Adresi"), [addresses]);
  const addressesAvailable = addresses.length > 0;
  const shouldShowSavedAddresses = isLoggedIn && addressesAvailable;
  const shouldShowAddressPrompt = isLoggedIn && !addressesAvailable;
  const shouldShowManualForm = !isLoggedIn;

  useEffect(() => {
    // Kayıtlı adresleri getir useEffect'i RTK Query tarafından yönetildiği için kaldırıldı veya basitleştirildi.
    // Hata yönetimi eklenebilir
    if (addressesError) {
      console.error("Adresler yüklenirken hata oluştu:", addressesError);
      showToast("Adresler yüklenirken bir hata oluştu", "error");
    }
  }, [isLoggedIn, addressesError, showToast]);

  useEffect(() => {
    setIller(Iller.iller);
  }, []);

  useEffect(() => {
    if (!addressesLoading && addresses.length > 0) {
      const defaultDelivery = deliveryAddresses[0] || addresses[0];
      if (defaultDelivery && !selectedDeliveryAddressId) {
        setSelectedDeliveryAddressId(defaultDelivery.id.toString());
      }

      if (!isBillingSame) {
        const defaultBilling = billingAddresses[0] || addresses.find(address => address.id !== defaultDelivery?.id);
        if (defaultBilling && !selectedBillingAddressId) {
          setSelectedBillingAddressId(defaultBilling.id.toString());
        }
      }
    }
  }, [addressesLoading, addresses, deliveryAddresses, billingAddresses, selectedDeliveryAddressId, selectedBillingAddressId, isBillingSame]);

  useEffect(() => {
    if (!shouldShowSavedAddresses) {
      return;
    }

    if (
      selectedDeliveryAddressId &&
      selectedBillingAddressId &&
      selectedDeliveryAddressId === selectedBillingAddressId &&
      !isBillingSame
    ) {
      setIsBillingSame(true);
    }
  }, [shouldShowSavedAddresses, selectedDeliveryAddressId, selectedBillingAddressId, isBillingSame]);

  const handleDeliverySelect = (e) => {
    const { value } = e.target;
    setSelectedDeliveryAddressId(value);
    if (isBillingSame) {
      setSelectedBillingAddressId(value);
    }
  };

  const validateManualForm = () => {
    if (!formData.ad.trim()) {
      showToast("Ad alanı zorunludur", "error");
      return false;
    }

    if (!formData.soyad.trim()) {
      showToast("Soyad alanı zorunludur", "error");
      return false;
    }

    if (!formData.email.trim()) {
      showToast("Email alanı zorunludur", "error");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      showToast("Geçerli bir email adresi giriniz", "error");
      return false;
    }

    const currentCountry = selectedCountry;
    const minLength = currentCountry.mobile_number_length || 10;

    if (!formData.telefon || formData.telefon.length < minLength) {
      showToast(`Telefon numarası en az ${minLength} haneli olmalıdır`, "error");
      return false;
    }

    if (!formData.il) {
      showToast("Lütfen bir il seçiniz", "error");
      return false;
    }

    if (!formData.ilce) {
      showToast("Lütfen bir ilçe seçiniz", "error");
      return false;
    }

    if (!formData.adres.trim() || formData.adres.trim().length < 10) {
      showToast("Adres en az 10 karakter olmalıdır", "error");
      return false;
    }

    return true;
  };

  const handleBillingSelect = (e) => {
    const { value } = e.target;
    setSelectedBillingAddressId(value);
  };

  const handleBillingSameToggle = () => {
    setIsBillingSame(prev => {
      const next = !prev;
      if (!next) {
        const defaultBilling = billingAddresses[0] || addresses.find(address => address.id.toString() !== selectedDeliveryAddressId);
        if (defaultBilling) {
          setSelectedBillingAddressId(defaultBilling.id.toString());
        } else {
          setSelectedBillingAddressId('');
        }
      } else {
        setSelectedBillingAddressId(selectedDeliveryAddressId);
      }
      return next;
    });
  };

  const handleAddressModalClose = () => {
    setShowAddressModal(false);
    setAddressModalMode('list');
    setEditingAddress(null);
    const defaultType = getAdresTipiByContext(addressModalContext);
    setAddressForm(getDefaultAddressForm(defaultType));
    setIlceler([]);
  };

  const handlePhoneKeyDown = (e) => {
    // Basic key handling if needed, but strict "05" enforcement is removed
  };

  const handlePhoneInput = (e, isModal = false) => {
    const { value } = e.target;
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    const currentCountry = isModal ? selectedCountryForModal : selectedCountry;
    const maxLength = currentCountry.mobile_number_length || 15;

    if (sanitizedValue.length > maxLength) {
      return;
    }

    if (isModal) {
      setAddressForm(prev => ({ ...prev, telefon: sanitizedValue }));
    } else {
      setFormData(prev => ({ ...prev, telefon: sanitizedValue }));
    }
  };

  const formatPhoneNumber = (number) => {
    return number; // Raw number for now, formatting can be added later based on country
  };

  const normalizeDateForInput = (value) => {
    if (!value) return '';

    const normalizeParts = (parts, order) => {
      const map = { day: parts[0], month: parts[1], year: parts[2] };
      if (order === 'YMD') {
        map.year = parts[0];
        map.month = parts[1];
        map.day = parts[2];
      } else if (order === 'DMY') {
        map.day = parts[0];
        map.month = parts[1];
        map.year = parts[2];
      }
      const { day, month, year } = map;
      if (!day || !month || !year) return '';
      return `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    };

    if (value instanceof Date && !isNaN(value)) {
      return value.toISOString().split('T')[0];
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return '';

      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
      }

      if (/^\d{2}[./-]\d{2}[./-]\d{4}$/.test(trimmed)) {
        const separator = trimmed.includes('.') ? '.' : trimmed.includes('/') ? '/' : '-';
        const parts = trimmed.split(separator);
        return normalizeParts(parts, 'DMY');
      }

      const parsed = new Date(trimmed);
      if (!isNaN(parsed)) {
        return parsed.toISOString().split('T')[0];
      }
    }

    return '';
  };

  const formatDateForDisplay = (value) => {
    const normalized = normalizeDateForInput(value);
    if (!normalized) return '-';

    const [year, month, day] = normalized.split('-');
    if (year && month && day) {
      return `${day}.${month}.${year}`;
    }

    return normalized;
  };

  const handleAddressFormChange = (e) => {
    const { name, value } = e.target;

    if (name === 'telefon') {
      handlePhoneInput(e, true);
      return;
    }

    if (name === "il") {
      const secilenIl = iller.find((il) => il.il_adi === value);
      setIlceler(secilenIl ? secilenIl.ilceler : []);
      setAddressForm(prev => ({
        ...prev,
        [name]: value,
        ilce: ""
      }));
    } else {
      setAddressForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const openAddressModal = (context) => {
    setAddressModalContext(context);
    const defaultType = getAdresTipiByContext(context);
    const contextAddresses =
      context === 'billing'
        ? (billingAddresses.length > 0 ? billingAddresses : addresses)
        : (deliveryAddresses.length > 0 ? deliveryAddresses : addresses);

    setAddressModalMode(contextAddresses.length > 0 ? 'list' : 'form');
    setEditingAddress(null);
    setAddressForm(getDefaultAddressForm(defaultType));
    setIlceler([]);
    setShowAddressModal(true);
  };

  const handleAddNewAddress = () => {
    const defaultType = getAdresTipiByContext(addressModalContext);
    setEditingAddress(null);
    setAddressForm(getDefaultAddressForm(defaultType));
    setIlceler([]);
    setAddressModalMode('form');
    setSelectedCountryForModal(countryCodes[0]);
  };

  const handleEditExistingAddress = (address) => {
    const defaultType = getAdresTipiByContext(addressModalContext);
    setEditingAddress(address.id);
    setAddressForm({
      id: address.id,
      title: address.title || "",
      ad: address.ad || "",
      soyad: address.soyad || "",
      email: address.email || "",
      telefon: address.telefon || "05",
      tc: address.tc || "",
      dogum_tarihi: normalizeDateForInput(address.dogum_tarihi),
      addressText: address.addressText || "",
      il: address.il || "",
      ilce: address.ilce || "",
      adres_tipi: address.adres_tipi || defaultType,
      ulke_kodu: address.ulke_kodu || "TR"
    });

    // Try to find the country code from the address or default to TR
    const country = countryCodes.find(c => c.code === (address.ulke_kodu || "TR"));
    if (country) {
      setSelectedCountryForModal(country);
    }

    if (address.il) {
      const secilenIl = iller.find((il) => il.il_adi === address.il);
      setIlceler(secilenIl ? secilenIl.ilceler : []);
    } else {
      setIlceler([]);
    }

    setAddressModalMode('form');
  };

  const handleAddressSelectFromModal = (addressId) => {
    const idString = addressId.toString();
    if (addressModalContext === 'delivery') {
      setSelectedDeliveryAddressId(idString);
      if (isBillingSame) {
        setSelectedBillingAddressId(idString);
      }
    } else {
      setSelectedBillingAddressId(idString);
    }
    handleAddressModalClose();
  };

  const handleAddressFormCancel = () => {
    const defaultType = getAdresTipiByContext(addressModalContext);
    const contextAddresses =
      addressModalContext === 'billing'
        ? (billingAddresses.length > 0 ? billingAddresses : addresses)
        : (deliveryAddresses.length > 0 ? deliveryAddresses : addresses);

    if (contextAddresses.length > 0) {
      setAddressModalMode('list');
      setEditingAddress(null);
      setAddressForm(getDefaultAddressForm(defaultType));
      setIlceler([]);
    } else {
      handleAddressModalClose();
    }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();

    if (addressForm.addressText.trim().length < 10) {
      showToast("Adres en az 10 karakter olmalıdır", "error");
      return;
    }

    if (!/^\d{11}$/.test(addressForm.tc)) {
      showToast("TC Kimlik numarası 11 haneli olmalıdır", "error");
      return;
    }

    if (!addressForm.dogum_tarihi) {
      showToast("Doğum tarihi alanı zorunludur", "error");
      return;
    }

    try {
      let response;
      if (editingAddress) {
        response = await updateAddress({ addressId: editingAddress, addressData: addressForm }).unwrap();
        showToast("Adres başarıyla güncellendi", "success");
      } else {
        response = await addAddress(addressForm).unwrap();
        showToast("Yeni adres başarıyla eklendi", "success");
      }

      await refetchAddresses();
      const createdId = response?.id ?? response?._id ?? response?.data?.id;
      const newId = (editingAddress || createdId)?.toString();

      if (addressModalContext === 'delivery') {
        if (newId) {
          setSelectedDeliveryAddressId(newId);
          if (isBillingSame) {
            setSelectedBillingAddressId(newId);
          }
        }
      } else if (newId) {
        setSelectedBillingAddressId(newId);
      }

      setAddressModalMode('list');
      const defaultType = getAdresTipiByContext(addressModalContext);
      setAddressForm(getDefaultAddressForm(defaultType));
      setEditingAddress(null);
      setIlceler([]);
    } catch (error) {
      console.error("Adres işlemi başarısız:", error);
      showToast("Adres işlemi sırasında bir hata oluştu", "error");
    }
  };

  const termMap = {
    'kullanım-kosullari': 'kullanim-kosullari',
    'mesafeli-satis': 'mesafeli-satis-sozlesmesi',
    'teslimat-iade': 'teslimat-ve-iade'
  };

  const getTermsContent = async (sef) => {
    try {
      const mappedTerm = termMap[sef];
      if (!mappedTerm) {
        throw new Error('Geçersiz terim');
      }

      const content = await fetchInstitutional(mappedTerm);

      if (!content || !content.baslik || !content.aciklama) {
        throw new Error('İçerik bulunamadı');
      }

      return (
        <div>
          <h2>{content.baslik}</h2>
          <div dangerouslySetInnerHTML={{ __html: content.aciklama }} />
        </div>
      );
    } catch (error) {
      console.error('Sözleşme içeriği yüklenirken hata:', error);
      return (
        <div>
          <p>Sözleşme içeriği yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.</p>
        </div>
      );
    }
  };

  useEffect(() => {
    const loadTermContent = async () => {
      if (modalIsOpen && currentTerm) {
        setIsLoading(true);
        try {
          const content = await getTermsContent(currentTerm);
          setTermContent(content);
        } catch (error) {
          console.error('Sözleşme içeriği yüklenirken hata:', error);
          setTermContent(<div>Sözleşme içeriği yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyiniz.</div>);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadTermContent();
  }, [modalIsOpen, currentTerm]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }

    if (name === 'telefon') {
      handlePhoneInput(e, false);
      return;
    }

    if (name === 'il') {
      const secilenIl = iller.find((il) => il.il_adi === value);
      setManualIlceler(secilenIl ? secilenIl.ilceler : []);
      setFormData(prev => ({ ...prev, il: value, ilce: '' }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Sözleşme kabul kontrolü
    if (!termsAccepted || !salesTermsAccepted || !deliveryTermsAccepted) {
      showToast("Lütfen tüm sözleşmeleri kabul edin.", "info");
      return;
    }

    if (!shouldShowSavedAddresses && shouldShowAddressPrompt) {
      showToast("Siparişe devam edebilmek için en az bir adres eklemelisiniz.", "error");
      return;
    }

    if (!shouldShowSavedAddresses) {
      if (!validateManualForm()) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      try {
        onSubmitSuccess({
          ...formData,
          teslimatAdresId: null,
          faturaAdresId: null,
          teslimatAdresi: null,
          faturaAdresi: null,
          faturamAyniAdres: true
        });
      } catch (error) {
        console.error('Form gönderme hatası:', error);
        showToast('İşlem sırasında bir hata oluştu', 'error');
      }
      return;
    }

    if (!selectedDeliveryAddressId) {
      showToast("Lütfen teslimat adresi seçiniz.", "error");
      return;
    }

    if (!isBillingSame && !selectedBillingAddressId) {
      showToast("Lütfen fatura adresi seçiniz.", "error");
      return;
    }

    const selectedDelivery = addresses.find(address => address.id.toString() === selectedDeliveryAddressId);
    const selectedBilling = isBillingSame
      ? selectedDelivery
      : addresses.find(address => address.id.toString() === selectedBillingAddressId);

    if (!selectedDelivery) {
      showToast("Seçili teslimat adresi bulunamadı.", "error");
      return;
    }

    if (!selectedBilling) {
      showToast("Seçili fatura adresi bulunamadı.", "error");
      return;
    }

    // Form geçerliyse, Card.js'e başarılı durumu bildir
    try {
      onSubmitSuccess({
        ...formData,
        ad: selectedDelivery.ad || '',
        soyad: selectedDelivery.soyad || '',
        email: selectedDelivery.email || '',
        telefon: selectedDelivery.telefon || '',
        il: selectedDelivery.il || '',
        ilce: selectedDelivery.ilce || '',
        adres: selectedDelivery.addressText || '',
        teslimatAdresId: selectedDelivery.id,
        faturaAdresId: selectedBilling.id,
        teslimatAdresi: selectedDelivery,
        faturaAdresi: selectedBilling,
        faturamAyniAdres: isBillingSame
      });
    } catch (error) {
      console.error('Form gönderme hatası:', error);
      showToast('İşlem sırasında bir hata oluştu', 'error');
    }
  };

  const renderAddressCard = ({ title, subtitle, selectedId, onChange, options, type, context }) => {
    const selectedAddress = addresses.find(address => address.id.toString() === selectedId);
    const hasAddress = options.length > 0 || addresses.length > 0;
    const buttonLabel = selectedAddress ? "Düzenle" : "Ekle";

    return (
      <div className="checkout-address-card">
        <div className="checkout-address-card-header">
          <div className="checkout-address-header-top">
            <div>
              <span className="checkout-address-type">{title}</span>
              <p className="checkout-address-subtitle">{subtitle}</p>
            </div>
            <button
              type="button"
              className="checkout-address-edit-button"
              onClick={() => openAddressModal(context)}
            >
              {buttonLabel}
            </button>
          </div>
          <div className="checkout-address-select-wrapper">
            <select value={selectedId} onChange={onChange} className="checkout-address-select">
              {(!hasAddress || options.length === 0) && (
                <option value="">{addressesLoading ? "Adresler yükleniyor..." : "Adres bulunamadı"}</option>
              )}
              {options.map(address => (
                <option key={address.id} value={address.id}>
                  {`${address.title || address.adres_tipi || ''} • ${address.il || ''} / ${address.ilce || ''}`.trim()}
                </option>
              ))}
            </select>
          </div>
        </div>
        {selectedAddress && (
          <div className="checkout-address-card-body">
            <div className="checkout-address-line">
              <span className="checkout-address-title">{selectedAddress.title}</span>
              <span className="checkout-address-location">
                {selectedAddress.ilce}, {selectedAddress.il}
              </span>
            </div>
            <p className="checkout-address-detail">{selectedAddress.addressText}</p>
            <div className="checkout-address-meta">
              <span>{selectedAddress.ad} {selectedAddress.soyad}</span>
              <span>{selectedAddress.telefon}</span>
            </div>
            <div className="checkout-address-meta">
              <span>TC: {selectedAddress.tc || "-"}</span>
              <span>Doğum Tarihi: {formatDateForDisplay(selectedAddress.dogum_tarihi)}</span>
            </div>
            <span className="checkout-address-pill">{type}</span>
          </div>
        )}
      </div>
    );
  };

  const modalTitle = addressModalContext === 'billing' ? "Fatura Adreslerim" : "Teslimat Adreslerim";
  const modalAddresses =
    addressModalContext === 'billing'
      ? (billingAddresses.length > 0 ? billingAddresses : addresses)
      : (deliveryAddresses.length > 0 ? deliveryAddresses : addresses);
  const activeModalAddressId =
    addressModalContext === 'billing' ? selectedBillingAddressId : selectedDeliveryAddressId;

  return (
    <div className='order-summary-second'>
      <form onSubmit={handleFormSubmit} className="checkout-form">
        <div className="checkout-form-group">
          <div className='siparis-adres-baslik'>
            <h3>Teslimat Bilgileri</h3>
          </div>
        </div>

        {/* Kayıtlı Adresler */}
        {shouldShowSavedAddresses && (
          <>
            {renderAddressCard({
              title: "Teslimat Adresi",
              subtitle: "Siparişiniz bu adrese teslim edilecek",
              selectedId: selectedDeliveryAddressId,
              onChange: handleDeliverySelect,
              options: deliveryAddresses.length > 0 ? deliveryAddresses : addresses,
              type: isBillingSame ? "Teslimat ile Fatura" : "Teslimat",
              context: "delivery"
            })}

            <div className="checkout-address-checkbox">
              <input
                type="checkbox"
                id="billing-same"
                checked={isBillingSame}
                onChange={handleBillingSameToggle}
              />
              <label htmlFor="billing-same">Faturamı Aynı Adrese Gönder</label>
            </div>

            {!isBillingSame && (
              renderAddressCard({
                title: "Fatura Adresi",
                subtitle: "Faturanız bu adrese gönderilecek",
                selectedId: selectedBillingAddressId,
                onChange: handleBillingSelect,
                options: billingAddresses.length > 0 ? billingAddresses : addresses,
                type: "Fatura",
                context: "billing"
              })
            )}
          </>
        )}

        {shouldShowAddressPrompt && (
          <div className="checkout-address-empty">
            <div>
              <h4>Henüz kayıtlı adresiniz bulunmuyor</h4>
              <p>Hızlıca adres ekleyerek siparişinizi tamamlayabilirsiniz.</p>
            </div>
            <button
              type="button"
              className="checkout-address-add-button"
              onClick={() => openAddressModal('delivery')}
            >
              Yeni Adres Ekle
            </button>
          </div>
        )}

        {shouldShowManualForm && (
          <>
            <div className="checkout-form-group d-flex">
              <div>
                <label>Ad*</label>
                <input
                  type="text"
                  name="ad"
                  value={formData.ad}
                  onChange={handleInputChange}
                  placeholder="Ad*"
                  required
                />
              </div>
              <div>
                <label>Soyad*</label>
                <input
                  type="text"
                  name="soyad"
                  value={formData.soyad}
                  onChange={handleInputChange}
                  placeholder="Soyad*"
                  required
                />
              </div>
            </div>

            <div className="checkout-form-group d-flex flex-column">
              <div>
                <label>Email*</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email*"
                  required
                />
              </div>
              <div style={{ flex: 1 }}>
                <label>Telefon*</label>
                <div className="phone-input-group">
                  <CountrySelector
                    selectedCountry={selectedCountry}
                    onSelect={(country) => {
                      setSelectedCountry(country);
                      setFormData(prev => ({ ...prev, telefon: '' }));
                    }}
                    countries={countryCodes}
                  />
                  <input
                    type="tel"
                    name="telefon"
                    value={formData.telefon}
                    onChange={(e) => handlePhoneInput(e, false)}
                    placeholder={selectedCountry.mobile_number_length ? `${selectedCountry.mobile_number_length} haneli` : "Telefon"}
                    maxLength={selectedCountry.mobile_number_length || 15}
                    required
                    className="phone-number-input py-4"
                  />
                </div>
              </div>
            </div>

            <div className="checkout-form-group d-flex">
              <select
                name="il"
                value={formData.il}
                onChange={handleInputChange}
                required
              >
                <option value="">Lütfen Bir İl Seçiniz</option>
                {iller.map((il, index) => (
                  <option key={index} value={il.il_adi}>
                    {il.il_adi}
                  </option>
                ))}
              </select>

              <select
                name="ilce"
                value={formData.ilce}
                onChange={handleInputChange}
                required
                disabled={!formData.il}
              >
                <option value="">Lütfen Bir İlçe Seçiniz</option>
                {manualIlceler.map((ilce, index) => (
                  <option key={index} value={ilce.ilce_adi}>
                    {ilce.ilce_adi}
                  </option>
                ))}
              </select>
            </div>

            <div className="checkout-form-group">
              <label>Sipariş Adresiniz*</label>
              <textarea
                name="adres"
                value={formData.adres}
                onChange={handleInputChange}
                placeholder="Sipariş Adresiniz"
                required
              />
            </div>
          </>
        )}

        <div className="checkout-form-group">
          <label>Sipariş Notunuz</label>
          <textarea
            name="siparis_notu"
            value={formData.siparis_notu}
            onChange={handleInputChange}
            placeholder="Sipariş Notunuz"
          />
        </div>

        <div className="payment-method-options">
          <h3>Ödeme Yöntemi</h3>
          <div className="payment-radio-container">
            <div className="payment-radio">
              <input
                type="radio"
                id="creditCard"
                name="paymentType"
                value="creditCard"
                checked={formData.paymentType === 'creditCard'}
                onChange={handleInputChange}
              />
              <label htmlFor="creditCard">Kredi Kartı ile Ödeme</label>
            </div>
            <div className="payment-radio">
              <input
                type="radio"
                id="eft"
                name="paymentType"
                value="eft"
                checked={formData.paymentType === 'eft'}
                onChange={handleInputChange}
              />
              <label htmlFor="eft">Havale/EFT ile Ödeme </label>
              {/* <span className='text-danger' style={{fontSize:'14px'}}>(%2 indirim)</span> */}

            </div>
          </div>
        </div>

        {referansKodu && (
          <div className="checkout-form-group ref-code" style={{ display: 'none' }}>
            <label>Referans Kodu</label>
            <input
              type="hidden"
              name="referansKodu"
              value={referansKodu}
            />
            <p className="referans-info">Bu sepet sizinle paylaşıldı. Referans kodu otomatik olarak kaydedilmiştir.</p>
          </div>
        )}

        <div className="checkbox-wrapper align-items-start">
          <input
            type="checkbox"
            className='mt-1'
            id="kullanim-kosullari"
            checked={termsAccepted}
            onChange={() => setTermsAccepted(!termsAccepted)}
          />
          <label htmlFor="kullanim-kosullari">
            <a href="#" onClick={(e) => {
              e.preventDefault();
              setCurrentTerm('kullanım-kosullari');
              setModalIsOpen(true);
            }}>Kullanım Koşulları</a> sözleşmesini okudum ve kabul ediyorum.
          </label>
        </div>

        <div className="checkbox-wrapper align-items-start">
          <input
            type="checkbox"
            className='mt-1'
            id="mesafeli-satis"
            checked={salesTermsAccepted}
            onChange={() => setSalesTermsAccepted(!salesTermsAccepted)}
          />
          <label htmlFor="mesafeli-satis">
            <a href="#" onClick={(e) => {
              e.preventDefault();
              setCurrentTerm('mesafeli-satis');
              setModalIsOpen(true);
            }}>Mesafeli Satış</a> sözleşmesini okudum ve kabul ediyorum.
          </label>
        </div>

        <div className="checkbox-wrapper align-items-start">
          <input
            type="checkbox"
            className='mt-1'
            id="teslimat-iade"
            checked={deliveryTermsAccepted}
            onChange={() => setDeliveryTermsAccepted(!deliveryTermsAccepted)}
          />
          <label htmlFor="teslimat-iade">
            <a href="#" onClick={(e) => {
              e.preventDefault();
              setCurrentTerm('teslimat-iade');
              setModalIsOpen(true);
            }}>Teslimat ve İade</a> sözleşmesini okudum ve kabul ediyorum.
          </label>
        </div>

        <button type="submit" className='button-apply' disabled={isDepositInCart}>
          {isDepositInCart ? 'Kaporalı ürün var' : 'Siparişi Tamamla'}
        </button>
      </form>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => {
          setModalIsOpen(false);
          setCurrentTerm('');
          setTermContent(null);
        }}
        contentLabel="Sözleşme Modal"
        className="Modal"
        overlayClassName="Overlay"
      >
        <button
          className="close-button"
          onClick={() => {
            setModalIsOpen(false);
            setCurrentTerm('');
            setTermContent(null);
          }}
        >
          ×
        </button>
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Sözleşme içeriği yükleniyor...</p>
          </div>
        ) : (
          termContent
        )}
      </Modal>

      {showAddressModal && (
        <div className="checkout-address-modal-overlay">
          <div className="checkout-address-modal">
            <div className="checkout-address-modal-header">
              <div>
                <h3>{modalTitle}</h3>
                <p className="checkout-address-modal-helper">
                  {modalAddresses.length > 0
                    ? "Adresinizi seçmek veya düzenlemek için kartlara dokunun."
                    : "Henüz bu tipte adresiniz bulunmuyor. Yeni adres ekleyebilirsiniz."}
                </p>
              </div>
              <div className="checkout-address-modal-header-actions">
                <button
                  type="button"
                  className="checkout-address-add-button"
                  onClick={handleAddNewAddress}
                >
                  Yeni Adres Ekle
                </button>
                <button
                  type="button"
                  className="checkout-address-close-button"
                  onClick={handleAddressModalClose}
                  aria-label="Kapat"
                >
                  ×
                </button>
              </div>
            </div>

            {addressModalMode === 'list' ? (
              addressesLoading ? (
                <div className="checkout-address-modal-empty">
                  <span>Adresler yükleniyor...</span>
                </div>
              ) : modalAddresses.length > 0 ? (
                <div className="checkout-address-modal-list">
                  {modalAddresses.map((address) => {
                    const addressIdString = address.id.toString();
                    const isSelected = activeModalAddressId === addressIdString;
                    return (
                      <div
                        key={address.id}
                        className={`checkout-address-modal-item${isSelected ? ' selected' : ''}`}
                        onClick={() => handleAddressSelectFromModal(address.id)}
                      >
                        <div className="checkout-address-modal-item-body">
                          <div className="checkout-address-modal-item-header">
                            <span className="checkout-address-modal-item-title">{address.title}</span>
                            <span className="checkout-address-modal-item-tag">
                              {address.ilce}, {address.il}
                            </span>
                          </div>
                          <p className="checkout-address-modal-item-text">{address.addressText}</p>
                          <div className="checkout-address-modal-item-meta">
                            <span>{address.ad} {address.soyad}</span>
                            <span>{address.telefon}</span>
                          </div>
                          <div className="checkout-address-modal-item-meta">
                            <span>TC: {address.tc || "-"}</span>
                            <span>Doğum: {formatDateForDisplay(address.dogum_tarihi)}</span>
                          </div>
                        </div>
                        <div className="checkout-address-modal-item-actions">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditExistingAddress(address);
                            }}
                          >
                            Düzenle
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="checkout-address-modal-empty">
                  <span>Henüz kayıtlı adresiniz bulunmuyor.</span>
                </div>
              )
            ) : (
              <form onSubmit={handleAddressSubmit} className="checkout-address-modal-form">
                <div className="checkout-address-form-header">
                  <button type="button" onClick={handleAddressFormCancel}>
                    ← Adres listesine dön
                  </button>
                  <span>{editingAddress ? "Adresi Düzenle" : "Yeni Adres Ekle"}</span>
                </div>

                <div className="checkout-address-form-field">
                  <label>Adres Başlığı</label>
                  <input
                    type="text"
                    name="title"
                    value={addressForm.title}
                    onChange={handleAddressFormChange}
                    placeholder="Örn: Ev, İş"
                    required
                  />
                </div>

                <div className="checkout-address-form-field">
                  <label>Adres Tipi</label>
                  <select
                    name="adres_tipi"
                    value={addressForm.adres_tipi}
                    onChange={handleAddressFormChange}
                    required
                  >
                    <option value="Teslimat Adresi">Teslimat Adresi</option>
                    <option value="Fatura Adresi">Fatura Adresi</option>
                  </select>
                </div>

                <div className="checkout-address-form-row">
                  <div className="checkout-address-form-field">
                    <label>Ad</label>
                    <input
                      type="text"
                      name="ad"
                      value={addressForm.ad}
                      onChange={handleAddressFormChange}
                      placeholder="Alıcı ad"
                      required
                    />
                  </div>
                  <div className="checkout-address-form-field">
                    <label>Soyad</label>
                    <input
                      type="text"
                      name="soyad"
                      value={addressForm.soyad}
                      onChange={handleAddressFormChange}
                      placeholder="Alıcı soyad"
                      required
                    />
                  </div>
                </div>

                <div className="checkout-address-form-row">
                  <div className="checkout-address-form-field">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={addressForm.email}
                      onChange={handleAddressFormChange}
                      placeholder="Email"
                      required
                    />
                  </div>
                  <div className="checkout-address-form-field">
                    <label>Telefon</label>
                    <div className="phone-input-group">
                      <CountrySelector
                        selectedCountry={selectedCountryForModal}
                        onSelect={(country) => {
                          setSelectedCountryForModal(country);
                          setAddressForm(prev => ({ ...prev, telefon: '', ulke_kodu: country.code }));
                        }}
                        countries={countryCodes}
                      />
                      <input
                        type="tel"
                        name="telefon"
                        value={addressForm.telefon}
                        onChange={(e) => handlePhoneInput(e, true)}
                        placeholder={selectedCountryForModal.mobile_number_length ? `${selectedCountryForModal.mobile_number_length} haneli` : "Telefon"}
                        maxLength={selectedCountryForModal.mobile_number_length || 15}
                        required
                        className="phone-number-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="checkout-address-form-row">
                  <div className="checkout-address-form-field">
                    <label>TC Kimlik</label>
                    <input
                      type="text"
                      name="tc"
                      value={addressForm.tc}
                      onChange={handleAddressFormChange}
                      placeholder="11 haneli TC kimlik"
                      maxLength="11"
                      required
                    />
                  </div>
                  <div className="checkout-address-form-field">
                    <label>Doğum Tarihi</label>
                    <input
                      type="date"
                      name="dogum_tarihi"
                      value={addressForm.dogum_tarihi}
                      onChange={handleAddressFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="checkout-address-form-row">
                  <div className="checkout-address-form-field">
                    <label>İl</label>
                    <select
                      name="il"
                      value={addressForm.il}
                      onChange={handleAddressFormChange}
                      required
                    >
                      <option value="">İl Seçiniz</option>
                      {iller.map((il, index) => (
                        <option key={index} value={il.il_adi}>
                          {il.il_adi}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="checkout-address-form-field">
                    <label>İlçe</label>
                    <select
                      name="ilce"
                      value={addressForm.ilce}
                      onChange={handleAddressFormChange}
                      required
                      disabled={!addressForm.il}
                    >
                      <option value="">İlçe Seçiniz</option>
                      {ilceler.map((ilce, index) => (
                        <option key={index} value={ilce.ilce_adi}>
                          {ilce.ilce_adi}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="checkout-address-form-field">
                  <label>Adres</label>
                  <textarea
                    name="addressText"
                    value={addressForm.addressText}
                    onChange={handleAddressFormChange}
                    placeholder="Adresinizi giriniz"
                    required
                  ></textarea>
                </div>

                <div className="checkout-address-modal-actions">
                  <button type="button" className="checkout-address-cancel-button" onClick={handleAddressFormCancel}>
                    Vazgeç
                  </button>
                  <button
                    type="submit"
                    className="checkout-address-save-button"
                    disabled={isAddingAddress || isUpdatingAddress}
                  >
                    {isAddingAddress || isUpdatingAddress ? "Kaydediliyor..." : editingAddress ? "Güncelle" : "Kaydet"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryForm; 